import { IconButton } from '@/shared/ui/primitives';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import {
  ActionHistoryLink,
  ActionLogEmpty,
  ActionTimeline,
  actionLogsQueryOptions,
  getActionLogPreview,
} from '@/features/action-log';
import { paths, type ClientHistoryLocationState } from '@/navigation/paths';
import { QueryBoundary, SectionRow } from '@/shared/ui';
import { TEST_IDS } from '@/testIds';
import styles from './ClientHistorySection.module.css';

const HISTORY_PREVIEW_LIMIT = 5;

interface ClientHistorySectionProps {
  clientId: string;
}

export const ClientHistorySection: FC<ClientHistorySectionProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const openHistorySearch = () => {
    const state: ClientHistoryLocationState = { focusSearch: true };
    void navigate(paths.clientHistory(clientId), { state });
  };

  return (
    <div className={styles.block}>
      <SectionRow
        className={styles.sectionHeader}
        label="History"
        labelTestId={TEST_IDS.historySectionHeader}
        action={
          <IconButton
            size="md"
            variant="surface"
            onClick={openHistorySearch}
            aria-label="Search history"
            data-testid={TEST_IDS.historySectionSearchButton}
          >
            <Search size={24} aria-hidden="true" />
          </IconButton>
        }
      />
      <div className={styles.body}>
        <QueryBoundary
          resetKeys={[clientId]}
          loadingFallback={<ActionLogEmpty hint="Loading history..." />}
          errorFallback={() => <ActionLogEmpty hint="History is unavailable" />}
        >
          <ClientHistoryPreview clientId={clientId} />
        </QueryBoundary>
      </div>
    </div>
  );
};

const ClientHistoryPreview: FC<ClientHistorySectionProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(actionLogsQueryOptions.byClient(clientId));
  const preview = useMemo(
    () => getActionLogPreview(data.items, HISTORY_PREVIEW_LIMIT),
    [data.items]
  );

  return (
    <>
      <div className={styles.timelinePreview}>
        <ActionTimeline groups={preview.groups} />
      </div>
      {preview.totalHiddenCount > 0 && (
        <ActionHistoryLink onClick={() => void navigate(paths.clientHistory(clientId))} />
      )}
    </>
  );
};
